import type { AssessmentAttributes, UserAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'

import type { FormBuilderBlocks } from '@/components/UI/FormBuilder/types'

import AssessmentSessionQ from './AssessmentSessionQ'
import AssessmentSubmissionQ from './AssessmentSubmissionQ'
import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import SATAssessmentQ from './SATAssessmentQ'
import SeriesAssessmentQ from './SeriesAssessmentQ'
import SeriesQ from './SeriesQ'
import SessionQ from './SessionQ'
import UserQ from './UserQ'

export type AssessmentLearners = { user_id: number; display_name: string }[]

const AssessmentQ = {
  ...makeQueryModelV2<AssessmentAttributes>('assessment')([
    'id',
    'user_id',
    'title',
    'description',
    'form_blocks',
    'published_at',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),
  where: (
    {
      id,
      userId,
      title,
      published,
      include = [],
      paranoid = true,
      groupBy = null,
      order = null,
      limit = null,
    }: {
      id?: number | SQLFragment<number>
      userId?: number
      title?: string | SQLFragment<string>
      published?: boolean
      include?: IncludeAssociations
      paranoid?: boolean
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<AssessmentAttributes> => SQL`
        FROM ${AssessmentQ}
        ${includeAssociationsToSql(AssessmentQ, include)}
        WHERE
            ${id != null ? SQL`${AssessmentQ.id} = ${id} AND` : SQL``}
            ${userId != null ? SQL`${AssessmentQ.user_id} = ${userId} AND` : SQL``}
            ${title != null ? SQL`${AssessmentQ.title} = ${title} AND` : SQL``}
            ${
              published != null
                ? SQL`${AssessmentQ.published_at} IS${published ? SQL` NOT` : SQL``} NULL AND`
                : SQL``
            }
            ${paranoid ? SQL`${AssessmentQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
    `,

  associations: (): AssociationDefinitions => ({
    AssessmentSession: {
      query: AssessmentSessionQ,
      foreignKey: AssessmentSessionQ.assessment_id,
      sourceKey: AssessmentQ.id,
    },
    SeriesAssessment: {
      query: SeriesAssessmentQ,
      foreignKey: SeriesAssessmentQ.assessment_id,
      sourceKey: AssessmentQ.id,
    },
    AssessmentSubmission: {
      query: AssessmentSubmissionQ,
      foreignKey: AssessmentSubmissionQ.assessment_id,
      sourceKey: AssessmentQ.id,
    },
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: AssessmentQ.user_id,
    },
    SATAssessment: {
      query: SATAssessmentQ,
      foreignKey: SATAssessmentQ.assessment_id,
      sourceKey: AssessmentQ.id,
    },
  }),

  getViewersForAssessment: ({
    assessmentId,
    sessionId,
    userId = null,
  }: {
    assessmentId: number
    sessionId: number
    userId?: number | null
  }): SQLFragment<UserAttributes> =>
    UserQ.where(
      { id: userId },
      SQL`EXISTS(SELECT 1 ${AssessmentSessionQ.where({
        assessmentId,
        sessionId,
        include: [
          {
            association: 'Session',
            required: true,
            where: SessionQ.canUserAccessAssessments(UserQ.id),
          },
        ],
      })})`
    ),

  countSubmissions: (): SQLFragment<number> => SQL`
    COUNT(*) ${AssessmentSubmissionQ.where({ assessmentId: AssessmentQ.id })}
  `,

  getFormBlocksWithoutMetadata: (): SQLFragment<FormBuilderBlocks> => SQL`
    (select json_agg(e - 'metadata') from jsonb_array_elements(${AssessmentQ.form_blocks}) as e)
  `,

  isSATAssessment: (): SQLFragment<boolean> => SQL`
    EXISTS(SELECT 1 ${SATAssessmentQ.where({ assessmentId: AssessmentQ.id })})
  `,

  canBeManagedByUser: (userId: number): SQLFragment<boolean> => SQL`
   (
      ${AssessmentQ.user_id} = ${userId} OR

      EXISTS(SELECT 1 ${SeriesAssessmentQ.where({
        assessmentId: AssessmentQ.id,
        include: [
          { association: 'Series', required: true, where: SQL`${SeriesQ.isHost({ userId })}` },
        ],
      })}) OR

      EXISTS(SELECT 1 ${AssessmentSessionQ.where({
        assessmentId: AssessmentQ.id,
        include: [
          { association: 'Session', required: true, where: SQL`${SessionQ.isHost({ userId })}` },
        ],
      })})
  )
`,
}

export default AssessmentQ
